import React, { useCallback, useEffect, useMemo } from 'react';
import { useField, useFormState } from 'react-final-form';
import { observer } from 'mobx-react';
import dayjs from 'dayjs';
import { DateField } from 'ksnm-common-ui/lib/Form/DateField';
import { TextField } from 'ksnm-common-ui/lib/Form/InputFields';
import { SelectField } from 'ksnm-common-ui/lib/Form/SelectField';
import { TextAreaField } from 'ksnm-common-ui/lib/Form/TextAreaField';
import { createMask } from 'ksnm-common-ui/lib/MaskedInput';

import { fetchReferences } from '@stores/dictionaryStores/fetchReferences';
import { organizationsStore } from '@stores/dictionaryStores/organizationsStore';
import { periodsStore } from '@stores/dictionaryStores/periodsStore';
import { reportGroupsStore } from '@stores/dictionaryStores/reportGroupsStore/reportGroupsStore';
import { reportPeriodsStore } from '@stores/dictionaryStores/reportPeriodsStore';
import { publicationRegisterStore } from '@stores/publicationRegisterStore';

import { Info } from './Info';

import styles from '../AddPackageDrawer.module.scss';

const fields = [
  'organizationCode',
  'year',
  'periodCode',
  'reportGroupCode',
  'correction',
  'codeExt',
  'name',
  'description',
];

export const Form = observer(({ setTitle }): JSX.Element => {
  const { isFormed, setIsFormed, isEditPackageMode } = publicationRegisterStore;
  const { periods } = periodsStore;
  const { reportGroups } = reportGroupsStore;
  const { organizations } = organizationsStore;
  const { reportPeriods } = reportPeriodsStore;

  const { dirtyFieldsSinceLastSubmit } = useFormState();

  useEffect(() => {
    fetchReferences(['periods', 'reportGroups', 'reportPeriods']);
  }, []);

  useEffect(() => {
    if (
      !isEditPackageMode &&
      isFormed &&
      fields.some((field) => Object.keys(dirtyFieldsSinceLastSubmit).includes(field))
    ) {
      setIsFormed(false);
    }
  }, [dirtyFieldsSinceLastSubmit, isEditPackageMode, isFormed, setIsFormed]);

  const {
    input: { value: codeValue },
  } = useField('code');

  const {
    input: { value: reportGroupCodeValue, onChange: changeReportGroupCode },
  } = useField('reportGroupCode');

  const {
    input: { onChange: changePeriodCode },
  } = useField('periodCode');

  useEffect(() => {
    setTitle(isFormed ? codeValue : 'Создание пакета');
  }, [codeValue, isFormed, setTitle]);

  const onChangeReportGroup = useCallback(
    (value) => {
      changeReportGroupCode(value);
      changePeriodCode(undefined);
    },
    [changePeriodCode, changeReportGroupCode],
  );

  const renderName = useCallback((value) => `${value.code} - ${value.name}`, []);

  const periodsItems = useMemo(() => {
    if (reportGroupCodeValue) {
      const reportPeriodByGroupCode = reportPeriods
        .filter((period) => period.reportGroupCode === reportGroupCodeValue)
        .map((period) => period.periodCode);
      return periods.filter((period) => reportPeriodByGroupCode.includes(period.code));
    }
    return [];
  }, [periods, reportGroupCodeValue, reportPeriods]);

  const disabledDate = useCallback((currentDate) => {
    const year = dayjs(currentDate).year();
    return year < 2000 || year > 2100;
  }, []);

  const correctionMask = useMemo(() => createMask('NUMBER'), []);

  return (
    <>
      <div className={styles.row}>
        <SelectField
          label="Компания"
          name="organizationCode"
          items={organizations}
          size="small"
          required
          disabled={isEditPackageMode}
        />
        <DateField
          label="Год"
          name="year"
          dateFormat="YYYY"
          picker="year"
          size="small"
          required
          disabledDate={disabledDate}
          disabled={isEditPackageMode}
          fromYear={2000}
          toYear={2100}
        />
      </div>

      <div className={styles.row}>
        <SelectField
          label="Группа отчетов"
          name="reportGroupCode"
          items={reportGroups}
          size="small"
          renderName={renderName}
          required
          onChange={onChangeReportGroup}
          disabled={isEditPackageMode}
        />
        <SelectField
          label="Период"
          name="periodCode"
          items={periodsItems}
          size="small"
          renderName={renderName}
          required
          disabled={isEditPackageMode}
        />
      </div>

      <TextField
        label="Версия"
        name="correction"
        size="small"
        maxLength={2}
        required
        disabled={isEditPackageMode}
        min={0}
        max={99}
        mask={correctionMask}
        unmask={false}
      />
      <TextAreaField rows={1} label="Описание" name="description" size="small" maxLength={500} autoSize />

      <Info />
    </>
  );
});
